import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Mail, Phone, AlertTriangle } from 'lucide-react';
import { siteConfig } from '../data/content';

const NoticeOfPrivacyPracticesPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <FileText size={24} className="text-teal-700" />
            </div>
            <div>
              <h1 className="heading-1" data-testid="npp-title">Notice of Privacy Practices</h1>
              <p className="text-sm text-slate-500">{siteConfig.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            
            {/* Owner Review Required Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 flex items-start gap-4">
              <AlertTriangle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Document Under Review</h3>
                <p className="text-sm text-amber-800 mb-3">
                  This Notice of Privacy Practices is pending final review and approval by the practice owner 
                  and/or legal counsel. The complete, finalized notice will be available prior to your first appointment.
                </p>
                <p className="text-sm text-amber-800">
                  For current privacy information, please contact us directly at{' '}
                  <a href={`tel:${siteConfig.phone}`} className="font-medium underline">{siteConfig.phone}</a>.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
              
              {/* Header */}
              <div className="text-center pb-6 border-b border-slate-200">
                <h2 className="heading-2 mb-2">NOTICE OF PRIVACY PRACTICES</h2>
                <p className="body-medium text-slate-600">
                  THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED 
                  AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION.
                </p>
                <p className="body-medium text-slate-600 font-semibold mt-2">
                  PLEASE REVIEW IT CAREFULLY.
                </p>
              </div>

              {/* Our Commitment */}
              <div>
                <h3 className="heading-3 mb-4">Our Commitment to Your Privacy</h3>
                <p className="body-medium">
                  {siteConfig.name} is committed to protecting your health information. 
                  Federal and state laws require us to maintain the privacy of your protected 
                  health information (PHI) and to provide you with this notice explaining our 
                  legal duties and privacy practices.
                </p>
              </div>

              {/* How We May Use Your Information */}
              <div>
                <h3 className="heading-3 mb-4">How We May Use and Disclose Your Health Information</h3>
                <p className="body-medium mb-4">
                  We may use and disclose your health information for the following purposes:
                </p>
                
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-800 mb-2">Treatment</h4>
                    <p className="text-sm text-slate-600">
                      To provide, coordinate, or manage your healthcare and related services. 
                      This includes sharing information with other healthcare providers involved 
                      in your care.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-800 mb-2">Payment</h4>
                    <p className="text-sm text-slate-600">
                      To obtain payment for services we provide to you, including billing 
                      and collection activities and verification of insurance coverage.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-800 mb-2">Healthcare Operations</h4>
                    <p className="text-sm text-slate-600">
                      For our internal operations, including quality assessment, training, 
                      licensing, and other administrative activities.
                    </p>
                  </div>
                </div>
              </div>

              {/* Your Rights */}
              <div>
                <h3 className="heading-3 mb-4">Your Rights Regarding Your Health Information</h3>
                <p className="body-medium mb-4">You have the following rights:</p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li><strong>Right to Access:</strong> Request copies of your health information</li>
                  <li><strong>Right to Amend:</strong> Request corrections to your health information</li>
                  <li><strong>Right to an Accounting:</strong> Request a list of disclosures we have made</li>
                  <li><strong>Right to Request Restrictions:</strong> Ask us to limit how we use or disclose your information</li>
                  <li><strong>Right to Confidential Communications:</strong> Request that we communicate with you in a specific way or location</li>
                  <li><strong>Right to a Paper Copy:</strong> Obtain a paper copy of this notice</li>
                </ul>
              </div>

              {/* Complaints */}
              <div>
                <h3 className="heading-3 mb-4">Filing a Complaint</h3>
                <p className="body-medium">
                  If you believe your privacy rights have been violated, you may file a complaint 
                  with us or with the U.S. Department of Health and Human Services. We will not 
                  retaliate against you for filing a complaint.
                </p>
              </div>

              {/* Contact Information */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="heading-3 mb-4">Contact Us</h3>
                <p className="body-medium mb-4">
                  For questions about this notice or to exercise your rights, contact:
                </p>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-semibold text-slate-800">{siteConfig.name}</p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-teal-600" />
                      <a href={`tel:${siteConfig.phone}`} className="link text-sm">{siteConfig.phone}</a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-teal-600" />
                      <a href={`mailto:${siteConfig.email}`} className="link text-sm">{siteConfig.email}</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Effective Date - Pending */}
              <div className="text-center pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 italic">
                  Effective date will be established upon final owner/legal review.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NoticeOfPrivacyPracticesPage;
